// All data access for Posterly. Replaces the old Python backend:
// the site now talks straight to Supabase (database + storage + admin login).
import { supabase, IMAGE_BUCKET } from './supabase';

const COLUMNS =
  'id,name,price,description,category,image_url,image_width,image_height,images,sizes,trending,limited_edition,created_at';

// Shape rows exactly like the old API did, so existing pages keep working.
const normalize = (row) => ({
  ...row,
  price: Number(row.price),
  description: row.description || '',
  images: Array.isArray(row.images) ? row.images : [],
  sizes: Array.isArray(row.sizes)
    ? row.sizes.map((s) => ({ label: s.label, price: Number(s.price) }))
    : [],
});

const fail = (error, fallback) => {
  const err = new Error(error?.message || fallback);
  err.cause = error;
  throw err;
};

// ---------- Products (public) ----------
export async function fetchProducts({ category, search, minPrice, maxPrice } = {}) {
  let q = supabase.from('products').select(COLUMNS).order('created_at', { ascending: false });
  if (category) q = q.eq('category', category);
  if (search) q = q.ilike('name', `%${search}%`);
  if (minPrice != null) q = q.gte('price', minPrice);
  if (maxPrice != null) q = q.lte('price', maxPrice);
  const { data, error } = await q;
  if (error) fail(error, 'Could not load posters');
  return (data || []).map(normalize);
}

export async function fetchProduct(id) {
  const { data, error } = await supabase.from('products').select(COLUMNS).eq('id', id).maybeSingle();
  if (error) fail(error, 'Could not load poster');
  if (!data) {
    const err = new Error('Product not found');
    err.status = 404;
    throw err;
  }
  return normalize(data);
}

// ---------- Products (admin only — enforced by Supabase security rules) ----------
const toRow = (p) => {
  const row = {};
  [
    'name',
    'description',
    'category',
    'image_url',
    'image_width',
    'image_height',
    'images',
    'sizes',
    'trending',
    'limited_edition',
  ].forEach((k) => {
    if (p[k] !== undefined) row[k] = p[k];
  });
  if (p.price !== undefined) row.price = p.price; // DB recalculates from sizes anyway
  return row;
};

export async function createProduct(payload) {
  const { data, error } = await supabase.from('products').insert(toRow(payload)).select(COLUMNS).single();
  if (error) fail(error, 'Could not create poster');
  return normalize(data);
}

export async function updateProduct(id, payload) {
  const { data, error } = await supabase
    .from('products')
    .update(toRow(payload))
    .eq('id', id)
    .select(COLUMNS)
    .single();
  if (error) fail(error, 'Could not update poster');
  return normalize(data);
}

export async function deleteProduct(id) {
  const { error, count } = await supabase.from('products').delete({ count: 'exact' }).eq('id', id);
  if (error) fail(error, 'Could not delete poster');
  if (count === 0) throw new Error('Poster not found, or you are not allowed to delete it');
}

// ---------- Images ----------
// Any common image format is accepted (JPG, PNG, WEBP, GIF, BMP, AVIF, TIFF,
// iPhone HEIC/HEIF). Everything is resized to max 1200px and saved as WebP.
const MAX_INPUT_BYTES = 25 * 1024 * 1024; // original file; the WebP we store is far smaller
const MAX_DIMENSION = 1200; // sharp on screen, too small to print well — originals stay on your PC
const EXT_TYPES = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', jfif: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  gif: 'image/gif', bmp: 'image/bmp', avif: 'image/avif', tif: 'image/tiff', tiff: 'image/tiff',
  heic: 'image/heic', heif: 'image/heif',
};

const detectType = (file) => {
  const ext = (file.name || '').split('.').pop().toLowerCase();
  const t = (file.type || '').toLowerCase();
  if (t && t !== 'application/octet-stream') return t === 'image/jpg' ? 'image/jpeg' : t;
  return EXT_TYPES[ext] || '';
};

export const isSupportedImage = (file) => {
  const t = detectType(file);
  return t.startsWith('image/') && t !== 'image/svg+xml';
};

const loadImage = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this image. Try saving it as JPG or PNG and upload again.'));
    };
    img.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Turn any supported file into something the browser can draw on a canvas.
export async function decodeToDrawable(file) {
  const type = detectType(file);
  if (type === 'image/heic' || type === 'image/heif') {
    const { default: heic2any } = await import('heic2any');
    const out = await heic2any({ blob: file, toType: 'image/png' });
    return loadImage(Array.isArray(out) ? out[0] : out);
  }
  if (type === 'image/tiff') {
    const UTIF = (await import('utif')).default;
    const buf = await file.arrayBuffer();
    const ifds = UTIF.decode(buf);
    UTIF.decodeImage(buf, ifds[0]);
    const rgba = UTIF.toRGBA8(ifds[0]);
    const c = document.createElement('canvas');
    c.width = ifds[0].width;
    c.height = ifds[0].height;
    c.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(rgba), c.width, c.height), 0, 0);
    return c;
  }
  return loadImage(file);
}

export async function compressDrawable(src) {
  const srcW = src.naturalWidth || src.width;
  const srcH = src.naturalHeight || src.height;
  const scale = Math.min(1, MAX_DIMENSION / Math.max(srcW, srcH));
  const width = Math.round(srcW * scale);
  const height = Math.round(srcH * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(src, 0, 0, width, height);

  let blob = await canvasToBlob(canvas, 'image/webp', 0.85);
  let ext = 'webp';
  if (!blob || blob.type !== 'image/webp') {
    // Very old Safari can't make WebP — fall back to JPEG.
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.88);
    ext = 'jpg';
  }
  if (!blob) throw new Error('Failed to process image.');
  return { blob, ext, width, height };
}

async function compressImage(file) {
  return compressDrawable(await decodeToDrawable(file));
}

// Upload an already-processed image to an exact path (used by the bulk importer).
export async function uploadBlobAt(path, blob) {
  const { error } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(path, blob, { contentType: blob.type, cacheControl: '31536000', upsert: true });
  if (error) fail(error, 'Failed to store image. Please try again.');
  return supabase.storage.from(IMAGE_BUCKET).getPublicUrl(path).data.publicUrl;
}

export async function uploadImage(file) {
  if (!file) throw new Error('No file selected.');
  if (!isSupportedImage(file)) {
    throw new Error(`"${file.name}" is not an image file.`);
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error(
      `"${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum is 25MB.`
    );
  }
  const { blob, ext, width, height } = await compressImage(file);
  const id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `products/${id}.${ext}`;
  const { error } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(path, blob, { contentType: blob.type, cacheControl: '31536000', upsert: false });
  if (error) fail(error, 'Failed to store image. Please try again.');
  const { data } = supabase.storage.from(IMAGE_BUCKET).getPublicUrl(path);
  return { image_url: data.publicUrl, width, height };
}

// ---------- Admin auth (Supabase Auth) ----------
export async function adminSignIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) fail(error, 'Invalid credentials');
  return data.session;
}

export async function adminSignOut() {
  await supabase.auth.signOut();
}
